import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Archive, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import {
  useArchiveJournalTemplate,
  useCreateJournalTemplate,
  useJournalTemplates,
  useUpdateJournalTemplate,
  type JournalTemplateRow,
} from '@/hooks/useJournalTemplates';
import { canManageJournalTemplate, partitionJournalTemplates } from '@/utils/journalTemplates';

interface ManageJournalTemplatesModalProps {
  clinicId: string;
  isAdmin: boolean;
  onClose: () => void;
}

export function ManageJournalTemplatesModal({
  clinicId,
  isAdmin,
  onClose,
}: ManageJournalTemplatesModalProps) {
  const { t } = useTranslation();
  const templatesQuery = useJournalTemplates(clinicId);
  const createTemplate = useCreateJournalTemplate(clinicId);
  const updateTemplate = useUpdateJournalTemplate(clinicId);
  const archiveTemplate = useArchiveJournalTemplate(clinicId);

  const [editing, setEditing] = useState<JournalTemplateRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [scope, setScope] = useState<'personal' | 'clinic'>('personal');

  const templates = templatesQuery.data ?? [];
  const { personal, clinic } = partitionJournalTemplates(templates);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setContent('');
    setScope('personal');
    setEditing(null);
    setCreating(false);
  };

  const openCreate = () => {
    setTitle('');
    setDescription('');
    setContent('');
    setScope('personal');
    setEditing(null);
    setCreating(true);
  };

  const openEdit = (template: JournalTemplateRow) => {
    setTitle(template.title);
    setDescription(template.description ?? '');
    setContent(template.content_md);
    setScope(template.scope === 'clinic' ? 'clinic' : 'personal');
    setEditing(template);
    setCreating(false);
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    try {
      if (editing) {
        await updateTemplate.mutateAsync({
          id: editing.id,
          title: title.trim(),
          description: description.trim() || undefined,
          content_md: content,
          scope: isAdmin ? scope : 'personal',
        });
        toast.success(t('journal.template_updated'));
      } else {
        await createTemplate.mutateAsync({
          title: title.trim(),
          description: description.trim() || undefined,
          content_md: content,
          scope: isAdmin ? scope : 'personal',
        });
        toast.success(t('journal.template_created'));
      }
      resetForm();
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleArchive = async (template: JournalTemplateRow) => {
    try {
      await archiveTemplate.mutateAsync(template.id);
      toast.success(t('journal.template_archived'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const renderGroup = (label: string, rows: JournalTemplateRow[]) => (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-ds-muted">
        {label}
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-ds-muted">{t('journal.no_templates')}</p>
      ) : (
        rows.map((template) => (
          <div
            key={template.id}
            className="flex items-start gap-2 rounded-lg border border-ds-line p-3"
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-ds-text">{template.title}</p>
              {template.description && (
                <p className="mt-0.5 text-xs text-ds-muted">
                  {template.description}
                </p>
              )}
            </div>
            {canManageJournalTemplate(template, isAdmin) && (
              <>
                <Button
                  type="button"
                  variant="tertiary"
                  size="icon"
                  title={t('common.edit')}
                  onClick={() => openEdit(template)}
                >
                  <Pencil />
                </Button>
                <Button
                  type="button"
                  variant="tertiary"
                  size="icon"
                  title={t('journal.archive')}
                  onClick={() => handleArchive(template)}
                >
                  <Archive />
                </Button>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );

  const showForm = creating || Boolean(editing);

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-ds-scrim p-4">
      <div className="mx-auto mt-10 max-w-lg rounded-xl bg-ds-card shadow-pop">
        <div className="flex items-center justify-between border-b border-ds-line px-4 py-3">
          <h3 className="text-base font-medium text-ds-text">
            {t('journal.manage_templates')}
          </h3>
          <Button type="button" variant="link" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto p-4">
          {showForm ? (
            <div className="space-y-3">
              <Input
                label={t('journal.template_name')}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('journal.template_name_placeholder')}
              />
              <Input
                label={t('journal.template_description')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('journal.template_description_placeholder')}
              />
              <Textarea
                label={t('journal.content')}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={8}
                className="font-mono text-sm"
                placeholder={t('journal.content_placeholder')}
              />
              {isAdmin && (
                <div className="flex gap-4 text-sm">
                  <label className="flex items-center gap-1.5 text-ds-text">
                    <input
                      type="radio"
                      name="manageTemplateScope"
                      checked={scope === 'personal'}
                      onChange={() => setScope('personal')}
                      className="accent-ds-teal"
                    />
                    {t('journal.template_scope_personal')}
                  </label>
                  <label className="flex items-center gap-1.5 text-ds-text">
                    <input
                      type="radio"
                      name="manageTemplateScope"
                      checked={scope === 'clinic'}
                      onChange={() => setScope('clinic')}
                      className="accent-ds-teal"
                    />
                    {t('journal.template_scope_clinic')}
                  </label>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={resetForm}>
                  {t('common.cancel')}
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!title.trim() || createTemplate.isPending || updateTemplate.isPending}
                >
                  {createTemplate.isPending || updateTemplate.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    t('journal.save_template_confirm')
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-end">
                <Button size="sm" onClick={openCreate}>
                  <Plus className="h-4 w-4" />
                  {t('journal.new_template')}
                </Button>
              </div>
              {templatesQuery.isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-ds-muted" />
                </div>
              ) : (
                <div className="space-y-4">
                  {renderGroup(t('journal.my_templates'), personal)}
                  {renderGroup(t('journal.clinic_templates'), clinic)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
